// ============================================================
// src/components/MiniPlayer.js
//
// Spotify-style persistent playback bar. Floats above the
// bottom nav whenever a track is loaded in MusicPlayerContext.
// Disappears when stop() is called (currentTrack = null).
//
// ── WHAT'S IN THIS VERSION ───────────────────────────────────
//   Prev / Play-Pause / Next controls
//   Repeat button (none / all / one) matching GigDetails behaviour
//   Shuffle button — randomises next track selection
//   Clickable progress bar with seek
//   Track name + position in queue (e.g. "2 of 5")
//   Dark Spotify-style bar with hot-pink accent
//
// ── CSS ──────────────────────────────────────────────────────
//   All styles live in section 28 of index.css under the
//   .mini-player__ namespace. The only remaining inline style
//   is width on .mini-player__progress-fill because it is a
//   live percentage that changes every animation frame.
// ============================================================

import { useMusicPlayer } from '../context/MusicPlayerContext'

// ── Helper: format seconds → "m:ss" ─────────────────────────
function formatTime(secs) {
  if (!secs || isNaN(secs)) return '0:00'
  const m = Math.floor(secs / 60)
  const s = Math.floor(secs % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

export default function MiniPlayer() {

  const {
    currentTrack,
    isPlaying,
    progress,
    currentTime,
    duration,
    queue:          queueRaw,
    queueIndex:     queueIndexRaw,
    repeatMode:     repeatModeRaw,
    shuffleMode:    shuffleModeRaw,
    togglePlay,
    stop,
    seek,
    nextTrack:      nextTrackFn,
    prevTrack:      prevTrackFn,
    cycleRepeat:    cycleRepeatFn,
    toggleShuffle:  toggleShuffleFn,
  } = useMusicPlayer()

  // Safe fallbacks — the deployed MusicPlayerContext may not yet have these.
  // Defaults keep MiniPlayer from crashing when queue/repeat/shuffle
  // aren't available yet (e.g. after a partial deploy).
  const queue         = Array.isArray(queueRaw)             ? queueRaw        : []
  const queueIndex    = typeof queueIndexRaw  === 'number'  ? queueIndexRaw   : -1
  const repeatMode    = repeatModeRaw    || 'none'
  const shuffleMode   = shuffleModeRaw   ?? false
  const nextTrack     = typeof nextTrackFn    === 'function' ? nextTrackFn     : () => {}
  const prevTrack     = typeof prevTrackFn    === 'function' ? prevTrackFn     : () => {}
  const cycleRepeat   = typeof cycleRepeatFn  === 'function' ? cycleRepeatFn   : () => {}
  const toggleShuffle = typeof toggleShuffleFn === 'function' ? toggleShuffleFn : () => {}

  // Don't render if nothing is loaded
  if (!currentTrack) return null

  // ── Progress bar seek ────────────────────────────────────
  const handleProgressClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const pct  = ((e.clientX - rect.left) / rect.width) * 100
    seek(pct)
  }

  // ── Queue position label ─────────────────────────────────
  // Shows "2 of 5" when playing from a setlist queue
  const queueLabel = queue.length > 0 && queueIndex >= 0
    ? `${queueIndex + 1} of ${queue.length}`
    : null

  // ── Repeat button icon + active state ────────────────────
  const repeatIcon   = repeatMode === 'one' ? 'bi-repeat-1' : 'bi-arrow-repeat'
  const repeatActive = repeatMode !== 'none'

  // ── Prev/Next disabled states ─────────────────────────────
  // In shuffle mode prev/next are always enabled since the next
  // track is chosen randomly — there is no boundary to hit.
  // In normal mode disable at the start/end of the queue unless
  // repeat-all is on.
  const prevDisabled = !shuffleMode && (
    queue.length === 0 || (queueIndex <= 0 && repeatMode !== 'all')
  )
  const nextDisabled = !shuffleMode && (
    queue.length === 0 || (queueIndex >= queue.length - 1 && repeatMode !== 'all')
  )

  return (
    <div className="mini-player">

      {/* ── Main row: track info | controls | time ── */}
      <div className="mini-player__row">

        {/* Music note icon — substitutes for album art */}
        <div className="mini-player__art">
          <i className="bi bi-music-note-beamed mini-player__art-icon"></i>
        </div>

        {/* Track name + queue position */}
        <div className="mini-player__info">
          <div className="mini-player__track-name">
            {currentTrack.name}
          </div>
          {queueLabel && (
            <div className="mini-player__queue-label">
              {/* Show shuffle indicator next to queue position when active */}
              {shuffleMode && (
                <i className="bi bi-shuffle mini-player__shuffle-indicator"></i>
              )}
              {queueLabel}
            </div>
          )}
        </div>

        {/* ── Playback controls ── */}
        <div className="mini-player__controls">

          {/* Shuffle — toggles random next track selection */}
          <button
            onClick={toggleShuffle}
            title={shuffleMode ? 'Shuffle on — click to turn off' : 'Shuffle off — click to shuffle'}
            className={[
              'mini-player__icon-btn',
              'mini-player__icon-btn--sm',
              shuffleMode ? 'mini-player__icon-btn--active' : '',
            ].join(' ').trim()}
          >
            <i className="bi bi-shuffle"></i>
          </button>

          {/* Repeat — cycles none → all → one */}
          <button
            onClick={cycleRepeat}
            title={
              repeatMode === 'none' ? 'Repeat off — click to repeat all'
              : repeatMode === 'all'  ? 'Repeat all — click to repeat one'
              :                        'Repeat one — click to turn off'
            }
            className={[
              'mini-player__icon-btn',
              repeatActive ? 'mini-player__icon-btn--active' : '',
            ].join(' ').trim()}
          >
            <i className={`bi ${repeatIcon}`}></i>
          </button>

          {/* Previous track */}
          <button
            onClick={prevDisabled ? undefined : prevTrack}
            disabled={prevDisabled}
            title="Previous"
            className={[
              'mini-player__icon-btn',
              prevDisabled ? 'mini-player__icon-btn--disabled' : '',
            ].join(' ').trim()}
          >
            <i className="bi bi-skip-start-fill"></i>
          </button>

          {/* Play / Pause — larger circular button */}
          <button
            onClick={togglePlay}
            title={isPlaying ? 'Pause' : 'Play'}
            className="mini-player__play-btn"
          >
            <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
          </button>

          {/* Next track */}
          <button
            onClick={nextDisabled ? undefined : nextTrack}
            disabled={nextDisabled}
            title="Next"
            className={[
              'mini-player__icon-btn',
              nextDisabled ? 'mini-player__icon-btn--disabled' : '',
            ].join(' ').trim()}
          >
            <i className="bi bi-skip-end-fill"></i>
          </button>

          {/* Close / stop */}
          <button
            onClick={stop}
            title="Stop and dismiss player"
            className="mini-player__icon-btn mini-player__icon-btn--close"
          >
            <i className="bi bi-x-lg"></i>
          </button>

        </div>

        {/* Time display */}
        <div className="mini-player__time">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

      </div>

      {/* ── Progress bar — sits at the very bottom of the bar ── */}
      <div
        onClick={handleProgressClick}
        title="Click to seek"
        role="slider"
        aria-label="Playback progress"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        className="mini-player__progress-track"
      >
        {/* width must stay inline — it's a live percentage updated every frame */}
        <div
          className="mini-player__progress-fill"
          style={{ width: `${progress}%` }}
        />
      </div>

    </div>
  )
}